import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXRay, faBrain } from '@fortawesome/free-solid-svg-icons';

const History = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/');
      } else {
        try {
          const q = query(
            collection(db, 'prediction_history'),
            where('userId', '==', user.uid),
            orderBy('date', 'desc')
          );
          const querySnapshot = await getDocs(q);
          const historyData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setHistory(historyData);
        } catch (err) {
          console.error('Error fetching history:', err);
          setErrorMessage("Failed to load prediction history. Please try again later.");
        } finally {
          setLoading(false);
        }
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Helper function to transform the result value
  const getLabelFromResult = (result, type) => {
    // If it's a brain tumor prediction, result is already in the correct format
    if (type === 'Brain MRI') {
      // Capitalize first letter of each word
      return result
        ? result.replace(/\b\w/g, (char) => char.toUpperCase())
        : 'Unknown';
    }
    
    // For X-ray pneumonia predictions
    switch (result?.toLowerCase()) {
      case 'no pneumonia detected':
        return 'Normal';
      case 'pneumonia detected':
        return 'Pneumonia';
      default:
        return result || 'Unknown Label';
    }
  };

  // Animation variants for Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="flex items-center space-x-2 text-gray-600">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
          <span className="text-lg font-semibold">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 font-['Noto Sans'] py-12 px-4 sm:px-6 lg:px-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-extrabold text-center mb-10 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
          <FontAwesomeIcon icon={faXRay} className="mr-3 text-purple-600" />
          Prediction History
        </h2>

        {errorMessage && (
          <div className="bg-red-100 rounded-2xl shadow-lg p-8 text-center border border-red-200">
            <p className="text-red-500 text-lg font-medium">
              {errorMessage}
            </p>
          </div>
        )}

        {history.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-200">
            <p className="text-gray-500 text-lg font-medium">
              No prediction history available.
            </p>
          </div>
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
          >
            {history.map((item) => (
              <motion.div
                key={item.id}
                className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300"
                variants={cardVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`bg-gradient-to-r ${item.type === 'Brain MRI' ? 'from-red-500 to-pink-500' : 'from-purple-500 to-blue-500'} p-3 rounded-full`}>
                    <FontAwesomeIcon 
                      icon={item.type === 'Brain MRI' ? faBrain : faXRay} 
                      className="text-white text-lg" 
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {item.labelName || 'Unknown ID'}: {getLabelFromResult(item.result, item.type)}
                  </h3>
                </div>
                <div className="space-y-2 text-gray-600">
                  <p>
                    <span className="font-medium">Type:</span> {item.type || 'N/A'}
                  </p>
                  <p>
                    <span className="font-medium">Confidence:</span> {item.confidence}%
                  </p>
                  <p>
                    <span className="font-medium">Date:</span>{' '}
                    {new Date(item.date).toLocaleString()}
                  </p>
                  <p>
                    <span className="font-medium">Credits Used:</span> {item.type === 'Brain MRI' ? 15 : 10}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default History;